import { useState, useEffect, useMemo } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import {
  TextInput,
  Checkbox,
  Paper,
  Button,
  Group,
  Stack,
  Text,
  Table,
  Badge,
  Collapse,
  Loader,
  ActionIcon,
  Alert,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronRight,
  IconSearch,
  IconX,
} from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useCanOrder } from './shared/useCanOrder'
import { SIGNATURE_REQUIRED_TOOLTIP } from './shared/constants'
import { TooltipButton } from './shared/TooltipButton'
import {
  GTI_QUESTIONNAIRE_ID,
  GTI_PANEL_COMPONENTS,
  GTI_FASTING_PANELS,
  GTI_SUB_PANELS,
  GTI_SUB_PANEL_CATEGORY,
  isGtiSubPanel,
  GTI_CMP_CODE,
  GTI_BMP_CODE,
  createInitialGtiFormState,
} from './gtiFormData'
import type { GtiFormState } from './gtiFormData'
import { validateGtiOrder } from './gtiOrderValidation'
import { OrderFormLayout } from './shared/OrderFormLayout'
import { OrderCart } from './shared/OrderCart'
import type { CartItem } from './shared/OrderCart'
import { OrderReviewSummary } from './shared/OrderReviewSummary'
import type { ReviewSection } from './shared/OrderReviewSummary'
import { useAutoPopulate } from './shared/useAutoPopulate'
import { useSignature } from './shared/useSignature'
import { useOrderSubmission } from './shared/useOrderSubmission'
import { FormHeader } from './shared/FormHeader'
import { SignatureSection } from './shared/SignatureSection'
import { DiagnosisCodesSection } from './shared/DiagnosisCodesSection'
import { generateBeHumanOrderPdf } from './pdf/behumanPdfGenerator'
import { mapGtiToPdf } from './pdf/mapGtiToPdf'
import type { AutoPopulateData } from './pdf/pdfTypes'
import { useTestCatalog } from './shared/useTestCatalog'
import type { CatalogTest } from './shared/useTestCatalog'

type Props = {
  patient: Patient
  onBack: () => void
}

const PANEL_CATEGORY = 'blood-test-panel'
const INDIVIDUAL_CATEGORY = 'individual-test'

export const GtiRequisitionForm = ({ patient, onBack }: Props) => {
  const {
    patientDefaults,
    providerDefaults,
    insuranceDefaults,
    diagnosisDefaults,
    orgError,
    insuranceWarning,
    insuranceResolved,
  } = useAutoPopulate(patient)
  const signature = useSignature()
  const { canOrder: isPractitioner, loading: canOrderLoading } = useCanOrder()

  const { tests: allTests, loading: testsLoading } = useTestCatalog('gti', { _count: 200 })

  const testMap = useMemo(() => {
    const map = new Map<string, CatalogTest>()
    for (const t of allTests) map.set(t.code, t)
    return map
  }, [allTests])

  const panelTests = useMemo(
    () => allTests.filter((t) => t.category === PANEL_CATEGORY),
    [allTests],
  )

  // Only offer sub panels the catalog resolved. They are listed from a constant,
  // so without this a failed catalog request would leave these four selectable
  // while the panels and individual tests correctly vanished — and the order
  // would carry a test the catalog could not describe.
  const subPanels = useMemo(
    () => GTI_SUB_PANELS.filter((subPanel) => testMap.has(subPanel.code)),
    [testMap],
  )

  // The requisition prints its individual tests alphabetically in three columns;
  // grouping them the way the AccuReference form does keeps a 59-row list scannable.
  const individualGroups = useMemo(() => {
    const groups: { category: string; tests: CatalogTest[] }[] = [
      { category: 'A – E', tests: [] },
      { category: 'F – L', tests: [] },
      { category: 'M – R', tests: [] },
      { category: 'S – Z', tests: [] },
    ]
    for (const test of allTests.filter((t) => t.category === INDIVIDUAL_CATEGORY)) {
      const first = test.name.charAt(0).toUpperCase()
      if (first <= 'E') groups[0].tests.push(test)
      else if (first <= 'L') groups[1].tests.push(test)
      else if (first <= 'R') groups[2].tests.push(test)
      else groups[3].tests.push(test)
    }
    return groups.filter((g) => g.tests.length > 0)
  }, [allTests])

  const form = useForm<GtiFormState>({
    initialValues: createInitialGtiFormState(),
    validateInputOnBlur: true,
  })

  useEffect(() => {
    if (providerDefaults.organizationName) {
      form.setValues({
        organizationName: providerDefaults.organizationName,
        orgPhone: providerDefaults.orgPhone,
        orgFax: providerDefaults.orgFax,
        orgAddress: providerDefaults.orgAddress,
        orgCity: providerDefaults.orgCity,
        orgState: providerDefaults.orgState,
        orgZip: providerDefaults.orgZip,
      })
    }
  }, [providerDefaults.organizationName]) // eslint-disable-line react-hooks/exhaustive-deps

  const autoPopulateData: AutoPopulateData = {
    patient: patientDefaults,
    provider: providerDefaults,
    insurance: insuranceDefaults,
  }

  const submission = useOrderSubmission({
    patientId: patient.id,
    questionnaireId: GTI_QUESTIONNAIRE_ID,
    generatePdfBytes: () =>
      generateBeHumanOrderPdf(mapGtiToPdf(form.values, autoPopulateData, testMap)),
    buildFileName: () =>
      `GTI_Order_${patientDefaults.lastName}_${patientDefaults.firstName}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`,
    getFormData: () => form.values as unknown as Record<string, unknown>,
    vendorName: 'GTI',
    laboratoryOrganizationName: 'GTI',
    diagnosisDefaults,
    getDiagnosisText: () => form.values.diagnosisText,
    orgError,
  })

  // Auto-apply saved signature
  useEffect(() => {
    if (signature.savedSignature && !form.values.signatureDataUrl) {
      form.setFieldValue('signatureDataUrl', signature.savedSignature)
      form.setFieldValue(
        'signatureDate',
        new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }),
      )
    }
  }, [signature.savedSignature]) // eslint-disable-line react-hooks/exhaustive-deps

  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [testSearch, setTestSearch] = useState('')
  const [showReview, setShowReview] = useState(false)

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })
  }

  const toggleTest = (code: string) => {
    const current = form.values.selectedTests
    const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code]
    form.setFieldValue('selectedTests', next)
  }

  const filteredIndividualGroups = useMemo(() => {
    if (!testSearch.trim()) return individualGroups
    const q = testSearch.toLowerCase()
    return individualGroups
      .map((group) => ({
        ...group,
        tests: group.tests.filter(
          (t) => t.name.toLowerCase().includes(q) || (t.specimen || '').toLowerCase().includes(q),
        ),
      }))
      .filter((group) => group.tests.length > 0)
  }, [testSearch, individualGroups])

  useEffect(() => {
    if (!testSearch.trim()) return
    setExpandedSections((prev) => {
      const next = new Set(prev)
      for (const group of filteredIndividualGroups) next.add(group.category)
      return next
    })
  }, [testSearch, filteredIndividualGroups])

  const fastingPanelSelected = form.values.selectedTests.some((c) =>
    (GTI_FASTING_PANELS as readonly string[]).includes(c),
  )
  // Blocking problems, recomputed as the form changes so the provider sees what
  // is missing before trying to submit rather than after.
  const problems = useMemo(
    () =>
      validateGtiOrder({
        // `insuranceWarning` is set by useAutoPopulate when the chart resolved no
        // active primary coverage — but only once the lookup finishes, so the
        // order must also wait for that rather than read "no warning yet" as
        // "insured".
        hasInsuranceOnFile: !insuranceWarning,
        insuranceResolved,
        diagnosisText: form.values.diagnosisText,
        selectedTests: form.values.selectedTests,
        signatureDataUrl: form.values.signatureDataUrl,
        patient: {
          dateOfBirth: patientDefaults.dateOfBirth,
          address: patientDefaults.address,
          phone: patientDefaults.phone,
          email: patientDefaults.email,
        },
      }),
    [
      form.values.diagnosisText,
      form.values.selectedTests,
      form.values.signatureDataUrl,
      insuranceWarning,
      insuranceResolved,
      patientDefaults,
    ],
  )

  const bothMetabolicPanelsSelected =
    form.values.selectedTests.includes(GTI_CMP_CODE) &&
    form.values.selectedTests.includes(GTI_BMP_CODE)

  const cartItems: CartItem[] = useMemo(
    () =>
      form.values.selectedTests.map((code) => {
        const test = testMap.get(code)
        // GTI prints no code for panel components, so the component name is the
        // only identity they have — it doubles as the cart's React key.
        const components = GTI_PANEL_COMPONENTS[code]
        // Group a sub panel as one, matching the requisition and GTI's own form
        // rather than leaving it filed under Individual Tests in the cart alone.
        const category = isGtiSubPanel(code)
          ? GTI_SUB_PANEL_CATEGORY
          : test?.categoryDisplay || 'Test'
        return {
          id: `${category}::${code}`,
          code,
          name: test?.name || code,
          category,
          ...(components ? { subItems: components.map((n) => ({ code: n, name: n })) } : {}),
        }
      }),
    [form.values.selectedTests, testMap],
  )

  const handleCartRemove = (id: string) => {
    const [, ...rest] = id.split('::')
    const code = rest.join('::')
    form.setFieldValue(
      'selectedTests',
      form.values.selectedTests.filter((c) => c !== code),
    )
  }

  const reviewSections: ReviewSection[] = useMemo(() => {
    const sections: ReviewSection[] = []
    const instructions = [
      { label: 'STAT', value: form.values.isStat ? 'Yes' : '' },
      { label: 'Fasting Required', value: form.values.isFasting ? 'Yes' : '' },
      { label: 'Venipuncture', value: form.values.isVenipuncture ? 'Yes' : '' },
    ]
    if (instructions.some((i) => i.value)) {
      sections.push({ title: 'Collection Instructions', items: instructions })
    }
    return sections
  }, [form.values])

  const handleSignatureConfirm = (dataUrl: string, date: string) => {
    form.setFieldValue('signatureDataUrl', dataUrl)
    form.setFieldValue('signatureDate', date)
    signature.closeSignatureModal()
  }

  /** Shared guard for Review and Submit. Both refuse an order GTI cannot process. */
  const validateBeforeSubmit = (action: 'reviewing' | 'submitting'): boolean => {
    const validation = form.validate()
    if (validation.hasErrors) {
      notifications.show({
        title: 'Validation Error',
        message: `Please fix the highlighted fields before ${action}.`,
        color: 'red',
      })
      return false
    }
    if (problems.length > 0) {
      notifications.show({
        title: problems.length === 1 ? 'Order incomplete' : `${problems.length} items to resolve`,
        message: problems.join(' '),
        color: 'red',
      })
      return false
    }
    return true
  }

  const handleReviewOrder = () => {
    if (validateBeforeSubmit('reviewing')) setShowReview(true)
  }

  const handleSubmitOrder = async () => {
    if (!validateBeforeSubmit('submitting')) return
    await submission.handleSubmitOrder()
  }

  // GTI orders by test name, so no code column: the catalog slugs are internal.
  const renderTestTable = (tests: CatalogTest[]) => {
    const hasSpecimen = tests.some((t) => t.specimen)
    return (
      <Table withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={40} />
            <Table.Th>Test Name</Table.Th>
            {hasSpecimen && <Table.Th w={110}>Specimen</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tests.map((test) => (
            <Table.Tr key={test.code}>
              <Table.Td>
                <Checkbox
                  aria-label={test.name}
                  checked={form.values.selectedTests.includes(test.code)}
                  onChange={() => toggleTest(test.code)}
                />
              </Table.Td>
              <Table.Td>
                <Text size="xs" fw={500}>
                  {test.name}
                </Text>
                {GTI_PANEL_COMPONENTS[test.code] && (
                  <Text size="xs" c="dimmed">
                    {GTI_PANEL_COMPONENTS[test.code].join(', ')}
                  </Text>
                )}
              </Table.Td>
              {hasSpecimen && (
                <Table.Td>
                  <Badge size="xs" variant="light" color="gray">
                    {test.specimen ?? '—'}
                  </Badge>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    )
  }

  const renderSectionToggle = (label: string, count: number) => (
    <Button
      variant="subtle"
      size="sm"
      fullWidth
      justify="space-between"
      rightSection={
        expandedSections.has(label) ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />
      }
      onClick={() => toggleSection(label)}
      className="!text-left"
    >
      <Group gap="xs">
        <Text fw={600} size="sm">
          {label}
        </Text>
        <Badge size="xs" variant="light">
          {count} tests
        </Badge>
      </Group>
    </Button>
  )

  return (
    <OrderFormLayout
      cart={<OrderCart items={cartItems} onRemove={handleCartRemove} vendorLabel="GTI" />}
      header={
        <FormHeader
          title="GTI Test Requisition for Blood"
          subtitle="Select tests and complete the order"
          onBack={onBack}
          onPreview={submission.handlePreviewPdf}
          onSubmit={handleSubmitOrder}
          exporting={submission.exporting}
          submitting={submission.submitting}
          orderSubmitted={submission.orderSubmitted}
          hideActions
          labError={submission.labError}
          orgError={orgError}
          warnings={insuranceWarning ? [insuranceWarning] : []}
        />
      }
    >
      {showReview ? (
        <Stack gap="lg">
          <OrderReviewSummary
            vendorName="GTI"
            tests={cartItems}
            diagnosisText={form.values.diagnosisText}
            sections={reviewSections}
            signatureDataUrl={form.values.signatureDataUrl}
            signatureDate={form.values.signatureDate}
            onEdit={() => setShowReview(false)}
            onSubmit={handleSubmitOrder}
            onPreview={submission.handlePreviewPdf}
            submitting={submission.submitting}
            exporting={submission.exporting}
            orderSubmitted={submission.orderSubmitted}
            isPractitioner={isPractitioner}
            canOrderLoading={canOrderLoading}
            labError={submission.labError}
            orgError={orgError}
          />
        </Stack>
      ) : (
        <Stack gap="lg">
          {testsLoading && (
            <Group justify="center" py="xl">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading test catalog...
              </Text>
            </Group>
          )}

          {!testsLoading && (
            <>
              {/* ===== ICD-10 / DIAGNOSES ===== */}
              <DiagnosisCodesSection
                value={form.values.diagnosisText}
                defaultCodes={diagnosisDefaults}
                onChange={(v) => form.setFieldValue('diagnosisText', v)}
              />

              {allTests.length === 0 && (
                <Alert
                  color="red"
                  icon={<IconAlertTriangle size={16} />}
                  title="GTI test catalog unavailable"
                >
                  The GTI catalog could not be loaded, so there are no tests to order. Reload the
                  page; if it persists the catalog may not be seeded for this environment.
                </Alert>
              )}

              {/* ===== BLOOD TEST PANELS ===== */}
              {panelTests.length > 0 && (
                <Paper p="md" withBorder>
                  <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                    BLOOD TEST PANELS
                  </Text>
                  <Text size="xs" c="dimmed" mb="sm">
                    Each panel lists the tests GTI runs as part of it.
                  </Text>
                  {renderTestTable(panelTests)}
                </Paper>
              )}

              {/* ===== SUB PANELS ===== */}
              {subPanels.length > 0 && (
                <Paper p="md" withBorder>
                  <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                    SUB PANELS
                  </Text>
                  <Text size="xs" c="dimmed" mb="sm">
                    Page 2 of GTI&rsquo;s requisition. Each sub panel is ordered as its individual
                    test, so selecting it here is the same order as selecting that test below.
                  </Text>
                  <Table withTableBorder withColumnBorders>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th w={40} />
                        <Table.Th>Sub Panel</Table.Th>
                        <Table.Th w={110}>Specimen</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {subPanels.map((subPanel) => (
                        <Table.Tr key={subPanel.code}>
                          <Table.Td>
                            <Checkbox
                              aria-label={subPanel.label}
                              checked={form.values.selectedTests.includes(subPanel.code)}
                              onChange={() => toggleTest(subPanel.code)}
                            />
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" fw={500}>
                              {subPanel.label}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {(GTI_PANEL_COMPONENTS[subPanel.code] ?? []).join(', ')}
                            </Text>
                            <Text size="xs" c="dimmed" fs="italic">
                              Ordered as {subPanel.orderedAs}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge size="xs" variant="light" color="gray">
                              {testMap.get(subPanel.code)?.specimen ?? '—'}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Paper>
              )}

              {/* ===== INDIVIDUAL TESTS ===== */}
              {individualGroups.length > 0 && (
                <Paper p="md" withBorder>
                  <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                    INDIVIDUAL TESTS
                  </Text>
                  <TextInput
                    placeholder="Search by test name or specimen..."
                    leftSection={<IconSearch size={16} />}
                    value={testSearch}
                    onChange={(e) => setTestSearch(e.target.value)}
                    mb="sm"
                    rightSection={
                      testSearch ? (
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          aria-label="Clear search"
                          onClick={() => setTestSearch('')}
                        >
                          <IconX size={14} />
                        </ActionIcon>
                      ) : null
                    }
                    rightSectionPointerEvents={testSearch ? 'all' : 'none'}
                  />
                  <Stack gap="xs">
                    {filteredIndividualGroups.map((group) => (
                      <div key={group.category}>
                        {renderSectionToggle(group.category, group.tests.length)}
                        <Collapse in={expandedSections.has(group.category)}>
                          <div className="mr-4 ml-4">{renderTestTable(group.tests)}</div>
                        </Collapse>
                      </div>
                    ))}
                  </Stack>
                </Paper>
              )}

              {/* GTI's own footnote — surfaced where the choice is made, and repeated
                  on the requisition by mapGtiToPdf. */}
              {bothMetabolicPanelsSelected && (
                <Alert
                  color="yellow"
                  icon={<IconAlertTriangle size={16} />}
                  title="CMP and BMP both selected"
                >
                  GTI Laboratories will only process CMP.
                </Alert>
              )}

              {/* ===== COLLECTION INSTRUCTIONS ===== */}
              <Paper p="md" withBorder>
                <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                  COLLECTION INSTRUCTIONS
                </Text>
                <Text size="xs" c="dimmed" mb="sm">
                  GTI&rsquo;s collector records the collection date, time, tubes drawn and their own
                  name at the draw. Use these to tell them how the draw should be done.
                </Text>
                <Group gap="lg">
                  <Checkbox
                    label="STAT"
                    checked={form.values.isStat}
                    onChange={(e) => form.setFieldValue('isStat', e.target.checked)}
                  />
                  <Checkbox
                    label="Fasting required"
                    checked={form.values.isFasting}
                    onChange={(e) => form.setFieldValue('isFasting', e.target.checked)}
                  />
                  <Checkbox
                    label="Venipuncture"
                    checked={form.values.isVenipuncture}
                    onChange={(e) => form.setFieldValue('isVenipuncture', e.target.checked)}
                  />
                </Group>

                {fastingPanelSelected && !form.values.isFasting && (
                  <Alert color="blue" mt="sm" icon={<IconAlertTriangle size={16} />}>
                    A selected panel is marked &ldquo;Fast Required&rdquo; on GTI&rsquo;s
                    requisition.
                  </Alert>
                )}
              </Paper>

              {/* ===== BLOCKING PROBLEMS ===== */}
              {problems.length > 0 && (
                <Alert
                  color="red"
                  icon={<IconAlertTriangle size={16} />}
                  title={
                    problems.length === 1
                      ? 'One item to resolve before ordering'
                      : `${problems.length} items to resolve before ordering`
                  }
                >
                  <Stack gap={2}>
                    {problems.map((problem) => (
                      <Text key={problem} size="sm">
                        {problem}
                      </Text>
                    ))}
                  </Stack>
                </Alert>
              )}

              {/* ===== SIGNATURE ===== */}
              <SignatureSection
                signatureDataUrl={form.values.signatureDataUrl}
                signatureDate={form.values.signatureDate}
                savedSignature={signature.savedSignature}
                signatureModalOpen={signature.signatureModalOpen}
                onOpenModal={signature.openSignatureModal}
                onCloseModal={signature.closeSignatureModal}
                onConfirm={handleSignatureConfirm}
                onSaveSignature={signature.handleSaveSignature}
                certificationText="Please order only if medically necessary and appropriate for individual patients."
              />

              {/* ===== ACTIONS ===== */}
              <Group justify="flex-end" pb="xl">
                <Button variant="default" onClick={onBack}>
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  onClick={submission.handlePreviewPdf}
                  loading={submission.exporting}
                >
                  Download PDF
                </Button>
                <TooltipButton
                  color="teal"
                  disabled={submission.orderSubmitted || problems.length > 0}
                  disabledReason={
                    problems.length === 0
                      ? null
                      : !form.values.signatureDataUrl && problems.length === 1
                        ? SIGNATURE_REQUIRED_TOOLTIP
                        : problems.join(' ')
                  }
                  onClick={handleReviewOrder}
                >
                  {submission.orderSubmitted ? 'Order Submitted' : 'Review Order'}
                </TooltipButton>
              </Group>
            </>
          )}
        </Stack>
      )}
    </OrderFormLayout>
  )
}
